import { useState } from "react";
import { cardImageSrc, CARD_ART_W, artWindow, artPortraitWindow, printedName } from "@/lib/artAssets";
import { categoryOfCardKey } from "@/lib/api";

// Square thumbnail window per art layout (see artWindow in lib/artAssets).
export const RESPONSE_KEYS = new Set(["protection_card"]);
const DOTS = { blue: "#3E7A92", green: "#5E7F4E", gray: "#8A8A84", action: "#9A6A4F" };

// Printed card type (as on the art): BUILDING / NATURE TILE / UPGRADE / ACTION.
const TYPE_LABEL = { building: "Building", nature: "Nature tile", upgrade: "Upgrade", action: "Action" };
const TYPE_OF_KEY = {
  house_cube: "building",
  urban_park: "nature", urban_forest: "nature", inland_wetland: "nature", mangrove_forest: "nature",
  green_roof: "upgrade", green_street: "upgrade", low_sea_wall: "upgrade",
};
export const cardTypeOf = (card) => (card?.kind === "action" ? "action" : card?.card_type || TYPE_OF_KEY[card?.key] || "building");
export const cardTypeLabel = (card) => TYPE_LABEL[cardTypeOf(card)] || "Card";

const groupOf = (card) => (card.kind === "build" ? card.category || categoryOfCardKey(card.key) : "action");

export const cardAlt = (card) => `${printedName(card)}. ${card.description || ""}`.trim();

// Category dot + printed card type ("NATURE TILE").
export function CategoryLine({ card, fontSize }) {
  const g = groupOf(card);
  return (
    <span className="flex items-center gap-1 min-w-0" data-testid="card-type-line" style={{ fontSize, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--ink-4)" }}>
      <span className="shrink-0" style={{ width: 6, height: 6, borderRadius: 3, background: DOTS[g] || DOTS.gray }} />
      <span className="truncate">{cardTypeLabel(card)}</span>
    </span>
  );
}

// Shows only the square artwork of the card PNG (cost badge printed in the art stays visible).
export function ArtCrop({ card, size, radius }) {
  const [failed, setFailed] = useState(false);
  const src = cardImageSrc(card.key);
  const win = artWindow(card.key);
  const w = (size * CARD_ART_W) / win.size;
  const offX = (size * win.x) / win.size;
  const offY = (size * win.y) / win.size;
  return (
    <span className="block relative overflow-hidden shrink-0" style={{ width: size, height: size, borderRadius: radius, background: "var(--line-soft)" }}>
      {src && !failed && (
        <img decoding="async"
          src={src}
          alt=""
          draggable={false}
          decoding="async"
          onError={() => setFailed(true)}
          className="absolute select-none"
          style={{ width: w, maxWidth: "none", height: "auto", left: -offX, top: -offY }}
        />
      )}
    </span>
  );
}

// Portrait thumbnail (fills a width x height box, card aspect) for the held action cards.
// Falls back to the whole card (contain) when the card has no portrait window.
export function ArtCropPortrait({ card, width, height }) {
  const [failed, setFailed] = useState(false);
  const src = cardImageSrc(card.key);
  const win = artPortraitWindow(card.key);
  if (!src || failed) return null;
  if (!win) {
    return <img decoding="async" src={src} alt="" draggable={false} onError={() => setFailed(true)} className="absolute inset-0 w-full h-full select-none" style={{ objectFit: "contain" }} />;
  }
  const k = width / win.w;
  return (
    <img decoding="async"
      src={src}
      alt=""
      draggable={false}
      decoding="async"
      data-testid="held-action-art"
      onError={() => setFailed(true)}
      className="absolute select-none"
      style={{ width: CARD_ART_W * k, maxWidth: "none", height: "auto", left: -win.x * k, top: -win.y * (height / win.h) }}
    />
  );
}
